import { useEffect, useState } from "react"

import acc from "accounting"
import { App, Flex, Modal, Spin, Tag, Typography } from "antd"
import cls from "classname"
import { useTranslation } from "react-i18next"
import { useDispatch, useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"

import InputComponent from "../../../../Components/Input"
import Icon from "../../../../Icon"
import Images from "../../../../Images"
import { normalizeMoney } from "../../../../Utils/normalizeMoney"
import { useQuery } from "../../../../Utils/utils"
import Creators from "../../reducer"
import { CircleInfoInfoCircleTooltipInformation, PAYMENT_METHODS, tooltipTitle } from "../helpers"
import { SelectableCard } from "../SelectableCard"

import "./styles.less"

const { Text, Link } = Typography

export const AddFundsModal = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { notification } = App.useApp()

  const { t } = useTranslation()
  const { pathname, search } = useLocation()
  const query = useQuery(search)
  const openAddFunds = query.get("open_add_funds")
  const fundsAdded = query.get("fundsAdded")

  const [value, setValue] = useState(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null)

  const { fetching, balance, saveFetching, saveResult, addFundsLoading } = useSelector(
    (state) => state.balance
  )
  const { userInfo } = useSelector((state) => state.auth)
  const isMobile = useSelector((state) => state.nav.isMobile)
  const addFundsOptions = balance?.add_funds_options ?? {}
  const { options = [], default: defaultPaymentMethod = null } = addFundsOptions
  const open = !!openAddFunds && balance?.["min_amount"] && !userInfo?.is_loyalty_billing

  const payVariantsList = balance?.["amounts_list"] || []
  const minAmount = balance?.["need_amount_validation"]
    ? balance?.["calculated_min_amount"] || 0
    : 0
  const normalizedValue = normalizeMoney(value)
  const hasValue = value !== null && value !== ""
  const isInvalidAmount = hasValue && !normalizedValue
  const isBelowMinAmount = !!normalizedValue && Number(normalizedValue) < minAmount

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  useEffect(() => {
    dispatch(Creators.resetAddFundsLoading())
  }, [])

  useEffect(() => {
    if (fundsAdded === "true" && saveResult === "balance_top_up") {
      handleCloseModal()
      notification.success({
        key: "balance-top-up",
        message: t("Balance updated"),
        description: t("Funds were added to your account."),
        ...notificationConfig
      })
      dispatch(Creators.balanceSetProp("saveResult", null))
    }
  }, [fundsAdded, saveResult])

  useEffect(() => {
    setSelectedPaymentMethod(defaultPaymentMethod)
  }, [defaultPaymentMethod])

  const handleCloseModal = () => {
    navigate(pathname, { replace: true })
    if (value) setValue(null)
  }

  const handleSubmitForm = () => {
    if (!hasValue || isInvalidAmount) return

    const amount = isBelowMinAmount ? normalizeMoney(minAmount) : normalizedValue

    dispatch(
      Creators.topUpBalanceRequest({
        amount,
        payment_provider: selectedPaymentMethod
      })
    )
  }

  const handleSetValue = (e) => {
    const normalizedValue = normalizeMoney(e.target.value)

    if (!normalizedValue) {
      setValue(null)
      return
    }

    if (Number(normalizedValue) < minAmount) {
      setValue(normalizeMoney(minAmount))
      return
    }

    setValue(normalizedValue)
  }

  return (
    <Modal
      open={open}
      centered={!isMobile}
      getContainer={() => document.getElementById("main-layout")}
      closeIcon={<Icon type="cross_small" />}
      width={442}
      title={isMobile ? null : t("Add funds")}
      closable={!isMobile}
      onCancel={handleCloseModal}
      okText={t("Continue")}
      onOk={handleSubmitForm}
      okButtonProps={{
        size: "large",
        disabled: !hasValue || isInvalidAmount || isBelowMinAmount,
        loading: fetching || saveFetching || addFundsLoading
      }}
      cancelButtonProps={{
        size: "large"
      }}
      destroyOnClose
      styles={{
        header: { padding: "0px 16px" },
        footer: { padding: "0px 16px" },
        content: { padding: "16px 0px" }
      }}
      classNames={{ content: "clear-bg" }}
      className="add-funds-modal"
    >
      <div style={{ position: "relative" }}>
        <div className="modal-info-note">
          <Icon type="circle_check_green" className="info-icon" style={{ marginRight: 8 }} />
          <Text>{t("Balances are refundable.")}</Text>&nbsp;
          <Link
            href={"https://help.chargeback.io/en/articles/12782877-what-is-balance-billing"}
            target="_blank"
            style={{ cursor: "pointer" }}
            className="link"
          >
            {t("See this page for limits.")}
          </Link>
        </div>
      </div>
      <div style={{ padding: "0 16px" }}>
        <Spin size="large" spinning={saveFetching}>
          <div className="top-wrapper">
            {isMobile && (
              <Flex justify="space-between" align="center">
                <Text className="add-funds-title">{t("Add funds")}</Text>
                <Icon type="cross_small" onClick={handleCloseModal} className="mobile-close-icon" />
              </Flex>
            )}
            <Text type="secondary" className="sub-title">
              {balance?.["need_amount_validation"]
                ? t("Enter an amount no less than $_min_", {
                    min: balance?.["min_amount"].toFixed(2)
                  })
                : t("Choose the amount of prepaid funds to add for your Chargeback account.")}
            </Text>
          </div>

          <Flex vertical gap={8} style={{ margin: !isMobile ? "52px 0 12px" : "16px 0 12px" }}>
            <Text strong>{t("Choose amount")}</Text>
            <InputComponent
              placeholder={0.0}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              inputMode="decimal"
              style={{ width: "100%" }}
              onBlur={handleSetValue}
            />
          </Flex>

          {payVariantsList?.map((v) => (
            <Tag
              key={v}
              size="large"
              onClick={() => setValue(v)}
              className={cls("funds-tag", { active: v === value })}
            >
              ${acc.formatNumber(v, 0, ",", ".")}
            </Tag>
          ))}

          <Flex vertical gap={8} style={{ margin: "12px 0 28px" }}>
            <Flex justify={"space-between"}>
              <Text type="secondary" strong>
                {t("Current")}
              </Text>
              <Text strong>${acc.formatNumber(balance?.["amount"], 2, ",", ".")}</Text>
            </Flex>

            <Flex justify={"space-between"}>
              <Flex gap={4}>
                <Text type="secondary" strong>
                  {t("Critical")}
                </Text>
                <CircleInfoInfoCircleTooltipInformation
                  title={t(tooltipTitle["critical"])}
                  isDanger={true}
                  overlayInnerStyle={{ width: 190 }}
                />
              </Flex>
              <Text strong>${acc.formatNumber(balance?.["critical_level"], 2, ",", ".")}</Text>
            </Flex>

            <Flex justify={"space-between"}>
              <Flex gap={4}>
                <Text type="secondary" strong>
                  {t("Minimum")}
                </Text>
                <CircleInfoInfoCircleTooltipInformation
                  title={t(tooltipTitle["min"])}
                  isDanger={false}
                  overlayInnerStyle={{ width: 190 }}
                />
              </Flex>
              <Text strong>
                ${acc.formatNumber(balance?.["calculated_min_amount"], 2, ",", ".")}
              </Text>
            </Flex>

            <Flex justify={"space-between"}>
              <Flex gap={4}>
                <Text type="secondary" strong>
                  {t("Recommended")}
                </Text>
                <CircleInfoInfoCircleTooltipInformation
                  title={t(tooltipTitle["rec"])}
                  isDanger={false}
                  overlayInnerStyle={{ width: 190 }}
                />
              </Flex>
              <Text strong>
                ${acc.formatNumber(balance?.["calculated_recommended"], 2, ",", ".")}
              </Text>
            </Flex>
            <Text strong>{t("Pay with")}</Text>
            <Flex vertical gap={8}>
              {options.includes(PAYMENT_METHODS.ADYEN) && (
                <SelectableCard
                  label="Adyen"
                  icon={<img width={20} src={Images.integration_adyen} />}
                  value={PAYMENT_METHODS.ADYEN}
                  selectedValue={selectedPaymentMethod}
                  onChange={setSelectedPaymentMethod}
                />
              )}
              {options.includes(PAYMENT_METHODS.STRIPE) && (
                <SelectableCard
                  label="Stripe"
                  icon={<img src={Images.integration_stripe} />}
                  value={PAYMENT_METHODS.STRIPE}
                  selectedValue={selectedPaymentMethod}
                  onChange={setSelectedPaymentMethod}
                />
              )}
              {options.includes(PAYMENT_METHODS.SHOPIFY) && (
                <SelectableCard
                  label="Shopify Payments"
                  icon={<img src={Images.shopify} />}
                  value={PAYMENT_METHODS.SHOPIFY}
                  selectedValue={selectedPaymentMethod}
                  onChange={setSelectedPaymentMethod}
                />
              )}
            </Flex>
          </Flex>
        </Spin>
      </div>
    </Modal>
  )
}
